import { NativeIconTheme } from "./native-icon-theme";
import {
  workspaceGridSettings,
  type WorkspaceGridSettings,
} from "../native/workspace-grid";
import "./native-interface-settings.css";
export interface InterfacePreferences {
  theme: "light" | "dark";
  left: boolean;
  right: boolean;
  scale: number;
  toolbar: string;
  workspace: string | null;
  workspaceGrid: WorkspaceGridSettings;
  icons: "classic" | "color";
  wheel: "wheel" | "alt-wheel";
}
const hex = (v: unknown): v is string =>
  typeof v === "string" && /^#[\da-f]{6}$/i.test(v);
export function readInterfacePreferences(): InterfacePreferences {
  const result: InterfacePreferences = {
    theme: "light",
    left: true,
    right: true,
    scale: 1,
    toolbar: "#20252d",
    workspace: null,
    workspaceGrid: workspaceGridSettings(null),
    icons: "classic",
    wheel: "wheel",
  };
  try {
    const v = JSON.parse(
      localStorage.getItem("creatorenginepro-native-ui-v1") ?? "null",
    );
    if (!v) return result;
    if (v.theme === "dark") result.theme = "dark";
    for (const side of ["left", "right"] as const)
      if (typeof v[side] === "boolean") result[side] = v[side];
    if (typeof v.scale === "number" && Number.isFinite(v.scale))
      result.scale = Math.max(1, Math.min(2, v.scale));
    if (hex(v.toolbar)) result.toolbar = v.toolbar;
    if (hex(v.workspace)) result.workspace = v.workspace;
    result.workspaceGrid = workspaceGridSettings(v.workspaceGrid);
    if (v.icons === "color") result.icons = "color";
    if (v.wheel === "alt-wheel") result.wheel = "alt-wheel";
  } catch {
    /* Optional browser preferences must never prevent editing. */
  }
  return result;
}
/** Original settings controls; their values belong to the browser, never the artwork. */
export class NativeInterfaceSettings {
  private dialog = document.getElementById(
    "native-settings-dialog",
  ) as HTMLDialogElement;
  private icons: NativeIconTheme;
  constructor(
    private root: HTMLElement,
    private preferences: InterfacePreferences,
    private changed: () => void,
    private workspaceColor: () => string,
  ) {
    this.icons = new NativeIconTheme(root);
    const field = (id: string) =>
      document.getElementById(id) as HTMLInputElement;
    field("creator-icon-size").value = String(
      Math.round(preferences.scale * 100),
    );
    field("creator-toolbar-color").value = preferences.toolbar;
    field("creator-icon-size").oninput = () => {
      preferences.scale = Number(field("creator-icon-size").value) / 100;
      this.apply();
    };
    field("creator-toolbar-color").oninput = () => {
      preferences.toolbar = field("creator-toolbar-color").value;
      this.apply();
    };
    this.dialog
      .querySelectorAll<HTMLButtonElement>(".settings-palette button")
      .forEach((b) => {
        const value = b.getAttribute("aria-label")!.split(" ").at(-1)!;
        b.onclick = () => {
          preferences.toolbar = value;
          field("creator-toolbar-color").value = value;
          this.apply();
        };
      });
    field("screen-inspector-pasteboard-color").oninput = () => {
      preferences.workspace = field("screen-inspector-pasteboard-color").value;
      this.apply();
    };
    this.dialog
      .querySelectorAll<HTMLButtonElement>(".screen-pasteboard-color")
      .forEach((b) => {
        b.onclick = () => {
          preferences.workspace = b.dataset.color!;
          this.apply();
        };
      });
    const gridEnabled = field("native-workspace-grid-enabled");
    const gridColor = field("native-workspace-grid-color");
    gridEnabled.onchange = () => {
      preferences.workspaceGrid.enabled = gridEnabled.checked;
      this.apply();
    };
    gridColor.oninput = () => {
      preferences.workspaceGrid.color = gridColor.value;
      preferences.workspaceGrid.opacity = Number(
        gridColor.dataset.colorAlpha ?? 1,
      );
      this.apply();
    };
    this.dialog
      .querySelectorAll<HTMLButtonElement>("[data-workspace-grid-color]")
      .forEach((button) => {
        button.onclick = () => {
          preferences.workspaceGrid.color = button.dataset.workspaceGridColor!;
          preferences.workspaceGrid.opacity = 1;
          this.apply();
        };
      });
    for (const [name, key] of [
      ["editor-icon-set", "icons"],
      ["editor-wheel-zoom-mode", "wheel"],
    ] as const)
      this.dialog
        .querySelectorAll<HTMLInputElement>(`[name="${name}"]`)
        .forEach((input) => {
          input.checked = input.value === preferences[key];
          input.onchange = () => {
            if (key === "icons")
              preferences.icons = input.value === "color" ? "color" : "classic";
            else
              preferences.wheel =
                input.value === "alt-wheel" ? "alt-wheel" : "wheel";
            this.apply();
          };
        });
    this.dialog.querySelector<HTMLButtonElement>(".settings-done")!.onclick =
      () => this.dialog.close();
    this.dialog.addEventListener("click", (e) => {
      if (e.target !== this.dialog) return;
      const r = this.dialog.getBoundingClientRect();
      if (
        e.clientX < r.left ||
        e.clientX > r.right ||
        e.clientY < r.top ||
        e.clientY > r.bottom
      )
        this.dialog.close();
    });
    this.dialog.addEventListener("keydown", (e) => e.stopPropagation());
    const gear = document.getElementById("native-settings")!;
    gear.setAttribute("aria-haspopup", "dialog");
    gear.setAttribute("aria-controls", this.dialog.id);
    this.dialog.addEventListener("close", () => {
      gear.setAttribute("aria-expanded", "false");
      gear.focus();
    });
    window.addEventListener("resize", () => this.layout());
    this.apply();
  }
  open() {
    this.syncWorkspace();
    this.dialog.showModal();
    document
      .getElementById("native-settings")!
      .setAttribute("aria-expanded", "true");
  }
  clearWorkspaceOverride() {
    this.preferences.workspace = null;
    this.apply();
  }
  private syncWorkspace() {
    const grid = this.preferences.workspaceGrid;
    (
      document.getElementById(
        "native-workspace-grid-enabled",
      ) as HTMLInputElement
    ).checked = grid.enabled;
    const gridColor = document.getElementById(
      "native-workspace-grid-color",
    ) as HTMLInputElement;
    gridColor.value = grid.color;
    gridColor.disabled = !grid.enabled;
    gridColor.dataset.colorAlpha = String(grid.opacity);
    (
      document.getElementById(
        "native-workspace-grid-colors",
      ) as HTMLFieldSetElement
    ).disabled = !grid.enabled;
    this.dialog
      .querySelectorAll<HTMLButtonElement>("[data-workspace-grid-color]")
      .forEach((button) => {
        button.setAttribute(
          "aria-pressed",
          String(
            button.dataset.workspaceGridColor === grid.color &&
              grid.opacity === 1,
          ),
        );
      });
    const color = this.preferences.workspace ?? this.workspaceColor();
    (
      document.getElementById(
        "screen-inspector-pasteboard-color",
      ) as HTMLInputElement
    ).value = color;
    this.dialog
      .querySelectorAll<HTMLElement>(".screen-pasteboard-color")
      .forEach((b) => {
        const active = b.dataset.color === color;
        b.classList.toggle("active", active);
        b.setAttribute("aria-pressed", String(active));
      });
  }
  private apply() {
    const p = this.preferences;
    this.root.style.setProperty("--toolbar", p.toolbar);
    const rgb = p.toolbar.match(/[a-f\d]{2}/gi)!.map((v) => parseInt(v, 16));
    this.root.style.setProperty(
      "--creator-toolbar-ink",
      rgb[0]! * 0.299 + rgb[1]! * 0.587 + rgb[2]! * 0.114 > 155
        ? "#243044"
        : "#f5f7fb",
    );
    (
      document.getElementById("creator-icon-size-value") as HTMLOutputElement
    ).value = Math.round(p.scale * 100) + "%";
    this.syncWorkspace();
    this.icons.set(p.icons);
    this.layout();
    this.changed();
  }
  private layout() {
    this.root.style.setProperty(
      "--creator-icon-scale",
      String(this.preferences.scale),
    );
    // Keep the enlarged original dock reachable on narrow screens without changing the camera.
    this.root.dataset.largeIcons = String(this.preferences.scale > 1);
  }
  render() {
    this.icons.sync();
  }
}
